import React, { useState } from 'react';
import { 
  DataGrid, 
  GridColDef, 
  GridActionsCellItem 
} from '@mui/x-data-grid';
import { 
  Box, 
  Button, 
  CircularProgress, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogTitle, 
  Stack, 
  TextField, 
  Typography
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { 
    useActualizarPlantaMutation, 
    useAgregarPlantaMutation, 
    useBorrarPlantaMutation, 
    useGetPlantasAllQuery 
} from '../../../api/frioApi';
import { useNavigate } from 'react-router-dom';
import { ItemPlanta } from '../interfaces/avanzado.interface';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { handleMutationError } from '../../../api/handleMutationError';

export const AvanzadoPlantaComponent: React.FC = () => {

  const navigate = useNavigate();

  const { data = [], isLoading, isError, error, refetch } = useGetPlantasAllQuery({});
  const [addItem, {isLoading: isLoadingAdd, isError: isErrorAdd, error: errorAdd, reset: resetAdd}] = useAgregarPlantaMutation();
  const [updateItem, {isLoading: isLoadingUpdate, isError: isErrorUpdate, error: errorUpdate}] = useActualizarPlantaMutation();
  const [deleteItem, {isLoading: isLoadingDelete, isError: isErrorDelete, error: errorDelete}] = useBorrarPlantaMutation();
  
  const [editItem, setEditItem] = useState<ItemPlanta | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [openDialogDelete, setOpenDialogDelete] = useState(false);
  const [selectedIdDelete, setSelectedIdDelete] = useState<string | null>(null);

  
  const handleAddClick = () => {
    setEditItem(null);
    setIsDialogOpen(true);
  };

  const handleEditClick = (item: ItemPlanta) => {
    setEditItem(item);
    setIsDialogOpen(true);
  };

  const handleDeleteClick = (id: string) => {
    setSelectedIdDelete(id);
    setOpenDialogDelete(true);
  };

  const confirmDelete = async () => {
    try {
      if (selectedIdDelete !== null) {
        await deleteItem(selectedIdDelete).unwrap();
        setOpenDialogDelete(false);
        setSelectedIdDelete(null);
        refetch();
      }
    } catch (err) {
      // Handle error, display message
      console.error("Error al elminar la planta", err);
    }   
  };

  const cancelDelete = () => {
    setOpenDialogDelete(false);
    setSelectedIdDelete(null);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setEditItem(null);
    resetAdd();
  };

  const handleSaveClick = async () => {
    try{
      if (editItem) {
        if (editItem.id) {
          await updateItem(editItem).unwrap();
        } else {
          await addItem(editItem).unwrap();
        }
        refetch();
        handleDialogClose();
      }
    } catch (err) {
      // Handle error, display message
      console.error("Error al guardar planta", err);
    }
  };

  const handleViewFriosClick = (planta: ItemPlanta) => {
    
    navigate("/avanzado/frigorificos",{state: {planta}});
  };

  const columns: GridColDef[] = [
    { field: 'name', headerName: 'Nombre',  flex: 1, minWidth: 150, editable: false },
    { field: 'name_front', headerName: 'Nombre Front',  flex: 1, minWidth: 150, editable: false },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Acciones',
      flex: 0.5,
      minWidth: 120,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<EditIcon />}
          color="primary"
          label="Edit"
          onClick={() => handleEditClick(params.row)}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          color="error"
          label="Delete"
          onClick={() => handleDeleteClick(params.row.id as string)}
        />,
      ],
    },
    { field: 'ubicacion', headerName: 'Ubicacion', flex: 1, minWidth: 120 , editable: false },
    { field: 'orden', headerName: 'Orden Aparicion', flex: 0.5, minWidth: 80, editable: false },
    { field: 'id', headerName: 'ID',  flex: 0.3, minWidth: 60},
    {
      field: 'ir',
      flex: 0.7,
      minWidth: 150,
      renderCell: (params) => (
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleViewFriosClick(params.row)}
          startIcon={<NavigateNextIcon />}  // Ícono que aparece al principio del botón
        >
          FRIGORIFICOS
        </Button>
      ),
    }
  ];

  return (
    <div>
    {isLoading ? (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    ) :
      <Box sx={{ height: '100%', width: '100%', padding: 2 }}>
        {/* Título del DataGrid */}
        <Typography variant="h5" component="h2" gutterBottom sx={{ textAlign: 'center' }}>
          Lista de plantas registradas
        </Typography>
        <Stack sx={{my: 2}} spacing={2} direction="row">
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddClick}
          >
            AGREGAR PLANTAS
          </Button>
        </Stack>
        <DataGrid
          rows={data!.data}
          columns={columns}
          editMode="row"
          onRowEditStop={() => {}}
        />
        
        <Dialog open={isDialogOpen} onClose={handleDialogClose}>
          <DialogTitle>{editItem ? 'Editar Planta' : 'Agregar Planta'}</DialogTitle>
          <DialogContent>
            {(isLoadingAdd || isLoadingUpdate) && <CircularProgress />}
            {isErrorAdd && <p>Error al guardar: {errorAdd? handleMutationError(errorAdd) : 'Falla no reconocida'}</p>}
            {isErrorUpdate && <p>Error al actualizar: {errorUpdate? handleMutationError(errorUpdate) : 'Falla no reconocida'}</p>}            
            <TextField
              autoFocus
              margin="dense"
              label="Nombre"
              fullWidth
              variant="standard"
              value={editItem?.name || ''}
              onChange={(e) => setEditItem({ ...editItem, name: e.target.value } as ItemPlanta)}
            />
            <TextField
              autoFocus
              margin="dense"
              label="Nombre Front"
              fullWidth
              variant="standard"
              value={editItem?.name_front || ''}
              onChange={(e) => setEditItem({ ...editItem, name_front: e.target.value } as ItemPlanta)}
            />
            <TextField
              autoFocus
              margin="dense"
              label="Ubicacion"
              fullWidth
              variant="standard"
              value={editItem?.ubicacion || ''}
              onChange={(e) => setEditItem({ ...editItem, ubicacion: e.target.value } as ItemPlanta)}
            />
            <TextField
              autoFocus
              margin="dense"
              label="Orden Aparición"
              fullWidth
              variant="standard"
              value={editItem?.orden || ''}
              onChange={(e) => setEditItem({ ...editItem, orden: e.target.value } as ItemPlanta)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose} color="primary">
              Cancelar
            </Button>
            <Button onClick={handleSaveClick} color="primary">
              Guardar
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={openDialogDelete} onClose={cancelDelete}>
          <DialogTitle>Confirmar eliminación</DialogTitle>
          <DialogContent>
            <Typography>
              ¿Está seguro de que desea eliminar la planta?
            </Typography>
          </DialogContent>
          <DialogActions>
            {isLoadingDelete && <CircularProgress />}
            {isErrorDelete && <p>Error al borrar: {errorDelete? handleMutationError(errorDelete) : 'Falla no reconocida'}</p>}            
            <Button onClick={cancelDelete} color="primary">
              Cancelar
            </Button>
            <Button onClick={confirmDelete} color="error">
              Eliminar
            </Button>
          </DialogActions>
        </Dialog>
    </Box>
    }
      {isError && <p>Falla Conexion con el servidor: {error? handleMutationError(error) : 'Falla no reconocida'}</p>}
    </div>
  );
};